// src/api/search.js — Algolia + LLM génériques (partagés Job, Dip, etc.)

import { api } from "../lib/apiClient";

const ALGOLIA_APP_ID = process.env.REACT_APP_ALGOLIA_APP_ID;
const ALGOLIA_SEARCH_KEY = process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY;

// ─── FILTRES ALGOLIA ──────────────────────────────────────────────────────────

/**
 * Construit la chaîne de filtres Algolia.
 * Si filterDimensions est fourni, expanse les valeurs canoniques vers leurs aliases
 * (ex: "BTS" → inclut aussi "BTS - BTSA - BTSM" dans la recherche Algolia).
 */
function buildAlgoliaFilters(filterState, filterDimensions = []) {
  const parts = [];
  for (const [key, vals] of Object.entries(filterState)) {
    if (!vals || vals.length === 0) continue;

    const dim = filterDimensions.find((d) => d.key === key);
    const aliasMap = dim?.aliases ?? {};

    // Expanse chaque valeur canonique pour inclure ses aliases inverses
    const expanded = [...new Set(
      vals.flatMap((canonical) => {
        const inverseAliases = Object.entries(aliasMap)
          .filter(([, target]) => target === canonical)
          .map(([alias]) => alias);
        return [canonical, ...inverseAliases];
      })
    )];

    if (expanded.length === 1) {
      parts.push(`${key}:"${expanded[0]}"`);
    } else {
      parts.push(`(${expanded.map((v) => `${key}:"${v}"`).join(" OR ")})`);
    }
  }
  return parts.join(" AND ");
}

export function countActiveFilters(filterState) {
  return Object.values(filterState).reduce((acc, vals) => acc + (vals?.length || 0), 0);
}

// ─── GÉOLOCALISATION ──────────────────────────────────────────────────────────

/**
 * Géocode une ville française via l'API Géo du gouvernement.
 * @returns {{ lat: number, lng: number } | null}
 */
export async function geocodeVille(ville) {
  if (!ville) return null;
  try {
    const res = await fetch(
      `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(ville)}&fields=centre,nom&limit=10&boost=population`
    );
    const data = await res.json();
    const match = data.find((c) => c.nom.toLowerCase() === ville.toLowerCase()) ?? data[0];
    if (match?.centre?.coordinates) {
      const [lng, lat] = match.centre.coordinates;
      return { lat, lng };
    }
  } catch { /* ignore */ }
  return null;
}

// ─── ALGOLIA SEARCH ───────────────────────────────────────────────────────────

/**
 * Requête Algolia unique.
 * @param {string} index
 * @param {string} ftsQuery
 * @param {object} filterState
 * @param {string[]} numericFilters
 * @param {string[]} facetKeys
 * @param {number} page
 * @param {object} geoOptions - { latLng: "lat,lng", radius: 30000 } | null
 * @param {Array} filterDimensions - pour l'expansion des aliases
 */
export async function searchAlgolia(index, ftsQuery, filterState, numericFilters, facetKeys, page = 0, geoOptions = null, filterDimensions = []) {
  const filters = buildAlgoliaFilters(filterState, filterDimensions);
  const body = {
    query: ftsQuery,
    hitsPerPage: 20,
    page,
    facets: facetKeys,
  };
  if (filters) body.filters = filters;
  if (numericFilters?.length) body.numericFilters = numericFilters;
  if (geoOptions?.latLng) {
    body.aroundLatLng = geoOptions.latLng;
    body.aroundRadius = geoOptions.radius ?? 30000;
  }

  const res = await fetch(
    `https://${ALGOLIA_APP_ID}-dsn.algolia.net/1/indexes/${index}/query`,
    {
      method: "POST",
      headers: {
        "X-Algolia-Application-Id": ALGOLIA_APP_ID,
        "X-Algolia-API-Key": ALGOLIA_SEARCH_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
  const data = await res.json();
  return {
    hits: data.hits || [],
    nbHits: data.nbHits || 0,
    nbPages: data.nbPages || 0,
    facets: data.facets || {},
  };
}

/**
 * Requêtes parallèles pour plusieurs villes, résultats mergés et dédupliqués.
 * Si geoLatLngs est vide, fait une recherche sans géoloc.
 */
export async function searchAlgoliaMulti(index, ftsQuery, filterState, geoLatLngs, geoRadius, numericFilters, facetKeys, page = 0, filterDimensions = []) {
  if (!geoLatLngs || geoLatLngs.length === 0) {
    return searchAlgolia(index, ftsQuery, filterState, numericFilters, facetKeys, page, null, filterDimensions);
  }
  if (geoLatLngs.length === 1) {
    return searchAlgolia(index, ftsQuery, filterState, numericFilters, facetKeys, page, { latLng: geoLatLngs[0], radius: geoRadius }, filterDimensions);
  }

  // Multi-villes : requêtes parallèles + déduplication
  const results = await Promise.all(
    geoLatLngs.map((latLng) =>
      searchAlgolia(index, ftsQuery, filterState, numericFilters, facetKeys, page, { latLng, radius: geoRadius }, filterDimensions)
    )
  );

  const seen = new Set();
  const hits = [];
  for (const result of results) {
    for (const hit of result.hits) {
      if (!seen.has(hit.objectID)) {
        seen.add(hit.objectID);
        hits.push(hit);
      }
    }
  }

  return {
    hits,
    nbHits: hits.length,
    nbPages: Math.max(...results.map((r) => r.nbPages)),
    facets: results[0]?.facets || {},
  };
}

// ─── LLM ─────────────────────────────────────────────────────────────────────

export async function callLLM(messages, assistantName) {
  const route = `/${assistantName}/algolia_query`;
  try {
    const { data } = await api.post(route, { messages });
    const raw = data.result?.content?.[0]?.text || "{}";
    const cleaned = raw.trim().replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "");
    try {
      return JSON.parse(cleaned);
    } catch (e) {
      console.error("[SEARCH] Erreur parse JSON →", e, "\nraw:", raw);
      return null;
    }
  } catch (e) {
    console.error("[SEARCH] Xano error →", e);
    return null;
  }
}
